import { useState } from "react";
import { X, Music, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Layout";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";

/** Post composer as a modal, opened from the floating "+" button instead of
 * a form pinned to the top of the feed — before this, publishing meant
 * scrolling all the way back up first, no matter how far down the feed you
 * were (user-reported UX issue, 2026-10-15). */
export default function ComposerModal({ myTracks, onClose, onPosted }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [caption, setCaption] = useState("");
  const [image, setImage] = useState("");
  const [trackId, setTrackId] = useState("");
  const [posting, setPosting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!caption.trim() && !image.trim() && !trackId) return;
    setPosting(true);
    try {
      await api.post("/posts", {
        caption: caption.trim(),
        image: image.trim() || null,
        track_id: trackId || null,
      });
      toast.success("Posted!");
      onPosted();
      onClose();
    } catch (e) {
      toast.error("Could not publish");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid="composer-modal">
      <div className="relative w-full max-w-lg max-h-[85dvh] bg-zinc-950 border border-white/10 rounded-3xl anim-in flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition z-10"
          data-testid="composer-close"
          aria-label="close"
        >
          <X className="w-4 h-4" />
        </button>

        <form onSubmit={submit} className="p-6 space-y-3 overflow-y-auto">
          <h2 className="text-xl font-black font-display text-white">{t.feed.publish}</h2>

          <div className="flex items-start gap-3 mt-2">
            <Avatar src={user.avatar} name={user.name} size="md" />
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={t.feed.compose}
              rows={3}
              autoFocus
              className="input-base resize-none flex-1"
              data-testid="composer-caption"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <FileUploader
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
              label={t.feed.photoLabel}
              dataTestid="composer-image-upload"
              preview={image}
              onUploaded={setImage}
              onClear={() => setImage("")}
            />
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4 text-zinc-500" />
              <select
                value={trackId}
                onChange={(e) => setTrackId(e.target.value)}
                className="input-base"
                data-testid="composer-track"
              >
                <option value="">{t.feed.trackLabel} — {t.feed.none}</option>
                {myTracks.map((tr) => (
                  <option key={tr.id} value={tr.id}>{tr.title}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={posting}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="composer-submit"
            >
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {t.feed.publish}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
